export type PlatformSkill = 'linkedin' | 'x' | 'newsletter' | 'blog' | 'presentation' | 'data-analysis'
export type PlatformMode = 'create' | 'coach' | 'plan'

export interface PlatformOutputInvocation {
  platform: PlatformSkill
  dossier: string
  auftrag: string
  mode: PlatformMode
  topicFreshness: boolean
  presentationFormat?: '16:9' | '4:5' | '1:1'
}

const PLATFORM_MODES = new Set<PlatformMode>(['coach', 'plan'])

export function parsePlatformOutputCommand(
  platform: PlatformSkill,
  rest: string,
): PlatformOutputInvocation | null {
  // /linkedin [coach|plan] [[<wikilink>]] [coach|plan] <auftrag> [--fresh]
  // /presentation "topic" --format=16:9|4:5|1:1
  let trimmed = rest.trim()
  if (!trimmed) return null

  let presentationFormat: PlatformOutputInvocation['presentationFormat']
  const formatMatch = trimmed.match(/(?:^|\s)--format(?:=|\s+)(16:9|4:5|1:1)\b/i)
  if (formatMatch?.[1]) {
    presentationFormat = formatMatch[1] as PlatformOutputInvocation['presentationFormat']
    trimmed = trimmed.replace(formatMatch[0], ' ').trim()
  }

  let topicFreshness = false
  if (/(?:^|\s)--fresh\b/.test(trimmed)) {
    topicFreshness = true
    trimmed = trimmed.replace(/(?:^|\s)--fresh\b/g, ' ').trim()
  }

  let mode: PlatformMode = 'create'
  const leadingMode = trimmed.match(/^(coach|plan)\b\s*(.*)$/i)
  if (leadingMode?.[1]) {
    const candidate = leadingMode[1].toLowerCase() as PlatformMode
    if (PLATFORM_MODES.has(candidate)) mode = candidate
    trimmed = leadingMode[2]!.trim()
  }

  const wikilinkMatch = trimmed.match(/^\[\[([^\]]+)\]\]\s*(.*)$/)
  if (!wikilinkMatch) {
    if (platform !== 'presentation') return null
    const quotedTopic = trimmed.match(/^"([^"]+)"\s*$/)
    const topic = (quotedTopic?.[1] ?? trimmed).trim()
    if (!topic) return null
    return {
      platform,
      dossier: topic,
      auftrag: `Erstelle eine Praesentation zu: ${topic}`,
      mode,
      topicFreshness,
      presentationFormat,
    }
  }
  const dossier = wikilinkMatch[1]!.trim()
  let after = wikilinkMatch[2]!.trim()
  if (!after && platform === 'data-analysis') {
    return {
      platform,
      dossier,
      auftrag: `Analysiere die Tabellen-Daten aus ${dossier}.`,
      mode,
      topicFreshness,
      presentationFormat,
    }
  }
  if (!after && platform === 'presentation') {
    // Kurzer, generierter Titel fuer den Auftrag; der lange Dossier-Pfad bleibt
    // im Backend als Quellenreferenz erhalten.
    const title = dossier.split('/').pop()?.replace(/\.md$/, '') ?? dossier
    return {
      platform,
      dossier,
      auftrag: `Erstelle eine Praesentation auf Basis des Dossiers: ${title}`,
      mode,
      topicFreshness,
      presentationFormat,
    }
  }
  if (!after) return null

  const firstSpace = after.indexOf(' ')
  const firstToken = firstSpace === -1 ? after.toLowerCase() : after.slice(0, firstSpace).toLowerCase()
  if (PLATFORM_MODES.has(firstToken as PlatformMode)) {
    mode = firstToken as PlatformMode
    if (firstSpace === -1) return null
    after = after.slice(firstSpace + 1).trim()
  }

  // Quotes entfernen
  const quotedMatch = after.match(/^"([^"]+)"\s*$/)
  const auftrag = quotedMatch ? quotedMatch[1]!.trim() : after.trim()
  if (!auftrag) return null

  return { platform, dossier, auftrag, mode, topicFreshness, presentationFormat }
}
